"use client";

import { ProtectRole } from "@/components/ProtectRole";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Switch } from "@/components/ui/switch";
import { ROLES } from "@/config/roles";
import { useAuth } from "@/hooks/use-auth";
import {
  useCopyPeriode,
  useMasterPeriode,
  useUpdateMasterPeriodePir,
} from "@/hooks/use-master-periode";
import { usePeriodeStore } from "@/hooks/use-periode";
import {
  Calendar,
  Check,
  Coins,
  Copy,
  Loader2,
  PenLine,
  Plus,
  RefreshCw,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";

export default function AppHeader() {
  const location = useLocation();
  const pathnames = location.pathname.split("/").filter((x) => x);
  const { user } = useAuth();
  const isAdminSuper = user?.role === ROLES.ADMIN_SUPER;
  const isDosen = user?.role === ROLES.DOSEN;
  const navigate = useNavigate();

  // --- State Management untuk Header ---
  const { activePeriod, setActivePeriod } = usePeriodeStore();
  const { data: masterPeriodes, isLoading: isMasterPeriodesLoading } =
    useMasterPeriode();
  const updatePirMutation = useUpdateMasterPeriodePir();
  const copyPeriodeMutation = useCopyPeriode();

  // Find current active period object
  const currentPeriodObj = masterPeriodes?.find(
    (p) => `${p.tahun}-${p.semester}` === activePeriod,
  );

  // Sort periods: Newest to Oldest
  const sortedPeriods = masterPeriodes?.slice().sort((a, b) => {
    if (a.tahun !== b.tahun) {
      return parseInt(b.tahun) - parseInt(a.tahun);
    }
    if (a.semester !== b.semester) {
      return b.semester - a.semester;
    }
    return b.master_periode_id - a.master_periode_id;
  });

  // Unique periods by Year and Semester, and ONLY show 2025 Semester 2
  const uniquePeriods = sortedPeriods?.filter(
    (item, index, self) =>
      index ===
      self.findIndex(
        (t) => t.tahun === item.tahun && t.semester === item.semester,
      ),
  );
  // .filter(
  //   (item) => item.tahun.toString() === "2025" && Number(item.semester) === 2,
  // );

  // Set default active period to 2025 semester 2, fallback to newest if not found
  useEffect(() => {
    if (uniquePeriods && uniquePeriods.length > 0) {
      if (!activePeriod) {
        const defaultPeriod =
          uniquePeriods.find(
            (p) => p.tahun.toString() === "2026" && Number(p.semester) === 1,
          ) || uniquePeriods[0];
        setActivePeriod(
          `${defaultPeriod.tahun}-${defaultPeriod.semester}`,
          defaultPeriod.master_periode_id,
        );
      } else {
        const current = uniquePeriods.find(
          (p) => `${p.tahun}-${p.semester}` === activePeriod,
        );
        if (
          current &&
          usePeriodeStore.getState().periodeId !== current.master_periode_id
        ) {
          setActivePeriod(activePeriod, current.master_periode_id);
        }
      }
    }
  }, [uniquePeriods, activePeriod, setActivePeriod]);

  // State untuk Modal Tambah Periode
  const [isAddPeriodModalOpen, setIsAddPeriodModalOpen] = useState(false);
  const [newPeriodData, setNewPeriodData] = useState({
    tahun: "",
    semester: "",
    pir: "4490",
    isActive: true,
  });

  const handleSaveNewPeriod = () => {
    console.log("Saving new period:", newPeriodData);
    setIsAddPeriodModalOpen(false);
    setNewPeriodData({
      tahun: "",
      semester: "",
      pir: "4490",
      isActive: true,
    });
  };

  // State untuk Modal Copy Periode
  const [isCopyPeriodModalOpen, setIsCopyPeriodModalOpen] = useState(false);
  const [copyData, setCopyData] = useState({
    source_periode_id: "",
    target_periode_id: "",
  });

  const handleSaveCopyPeriod = () => {
    if (copyData.source_periode_id && copyData.target_periode_id) {
      copyPeriodeMutation.mutate(
        {
          source_periode_id: parseInt(copyData.source_periode_id),
          target_periode_id: parseInt(copyData.target_periode_id),
        },
        {
          onSuccess: () => {
            setIsCopyPeriodModalOpen(false);
            setCopyData({
              source_periode_id: "",
              target_periode_id: "",
            });
          },
        },
      );
    }
  };

  // State untuk PIR (Pengali Indeks Rupiah)
  const [pirValue, setPirValue] = useState<number>(4490);
  const [isEditingPir, setIsEditingPir] = useState(false);
  const [draftPir, setDraftPir] = useState<string>(pirValue.toString());
  const pirInputRef = useRef<HTMLInputElement>(null);

  // Synchronize PIR value when period changes
  useEffect(() => {
    if (currentPeriodObj) {
      setPirValue(currentPeriodObj.pir);
      setDraftPir(currentPeriodObj.pir.toString());
    }
  }, [currentPeriodObj]);

  // Auto-focus saat masuk mode edit
  useEffect(() => {
    if (isEditingPir && pirInputRef.current) {
      pirInputRef.current.focus();
    }
  }, [isEditingPir]);

  // Format ke Rupiah
  const formatRupiah = (angka: number) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(angka);
  };

  // Handler Edit PIR
  const handleSavePir = () => {
    const parsed = parseInt(draftPir.replace(/\D/g, ""), 10);
    if (!isNaN(parsed) && parsed > 0 && currentPeriodObj) {
      updatePirMutation.mutate(
        {
          id: currentPeriodObj.master_periode_id,
          pir: parsed,
        },
        {
          onSuccess: () => {
            setPirValue(parsed);
            setIsEditingPir(false);
          },
        },
      );
    } else {
      setDraftPir(pirValue.toString()); // Revert jika invalid
      setIsEditingPir(false);
    }
  };

  const handleCancelPir = () => {
    setDraftPir(pirValue.toString());
    setIsEditingPir(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSavePir();
    if (e.key === "Escape") handleCancelPir();
  };

  return (
    <header className="sticky top-0 z-30 flex h-16 shrink-0 items-center justify-between gap-4 border-b bg-white/80 dark:bg-slate-950/80 backdrop-blur-md px-4 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12 shadow-sm">
      {/* --- KIRI: Navigasi & Breadcrumb --- */}
      <div className="flex items-center gap-2">
        <SidebarTrigger className="-ml-1 text-slate-500 hover:text-slate-900 dark:hover:text-slate-100" />
        <Separator orientation="vertical" className="mr-2 h-4 shadow-sm" />

        <Breadcrumb className="hidden md:block">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink
                onClick={() =>
                  navigate(`/${isDosen ? "/dosen" : "/admin"}/dashboard`)
                }
                className="text-xs font-medium uppercase tracking-wider text-slate-400"
              >
                Dashboard
              </BreadcrumbLink>
            </BreadcrumbItem>

            {pathnames.length > 0 && (
              <BreadcrumbSeparator className="text-slate-300" />
            )}

            {pathnames.map((name, index) => {
              const isLast = index === pathnames.length - 1;
              return (
                <div key={name} className="flex items-center gap-2">
                  <BreadcrumbItem>
                    {isLast ? (
                      <BreadcrumbPage className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-slate-100">
                        {name.replace(/-/g, " ")}
                      </BreadcrumbPage>
                    ) : (
                      <BreadcrumbLink className="text-xs font-medium uppercase tracking-wider text-slate-400">
                        {name.replace(/-/g, " ")}
                      </BreadcrumbLink>
                    )}
                  </BreadcrumbItem>
                  {!isLast && (
                    <BreadcrumbSeparator className="text-slate-300" />
                  )}
                </div>
              );
            })}
          </BreadcrumbList>
        </Breadcrumb>

        <Separator
          orientation="vertical"
          className="mx-2 h-4 shadow-sm hidden md:block"
        />

        <div className="flex items-center gap-2 md:flex">
          <img
            src="/logo-undiksha.png"
            alt="Logo Undiksha"
            className="h-7 w-auto object-contain"
          />
          <img
            src="/e-ganesha.png"
            alt="Logo E-Ganesha"
            className="h-7 w-auto object-contain"
          />
        </div>
      </div>

      {/* --- KANAN: Kontrol Global (PIR & Periode) --- */}
      <div className="flex items-center gap-3 lg:gap-4">
        {/* Tombol Reload Manual */}
        <Button
          variant="outline"
          size="sm"
          className="h-8 gap-1.5 bg-slate-50 border-slate-200 text-slate-600 hover:text-blue-600 hover:bg-blue-50"
          onClick={() => window.location.reload()}
          title="Muat Ulang Halaman"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          <span className="hidden sm:inline text-xs font-semibold">Reload</span>
        </Button>

        {/* 1. Pengaturan Nilai PIR */}
        <ProtectRole allowedRoles={[ROLES.ADMIN_SUPER]}>
          <div className="flex items-center">
            {isEditingPir ? (
              <div className="flex items-center gap-1 bg-emerald-50/50 p-1 border border-emerald-200 rounded-md shadow-sm transition-all">
                <span className="text-emerald-700 text-sm font-semibold pl-2">
                  Rp
                </span>
                <Input
                  ref={pirInputRef}
                  type="number"
                  value={draftPir}
                  onChange={(e) => setDraftPir(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="w-24 h-7 text-sm font-semibold text-emerald-800 bg-transparent border-none focus-visible:ring-0 focus-visible:ring-offset-0 p-0 text-right tabular-nums shadow-none"
                />
                <div className="flex items-center ml-1 bg-white rounded shadow-sm border border-emerald-100">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-none rounded-l"
                    onClick={handleSavePir}
                    disabled={updatePirMutation.isPending}
                  >
                    {updatePirMutation.isPending ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Check className="w-3.5 h-3.5" />
                    )}
                  </Button>
                  <Separator
                    orientation="vertical"
                    className="h-4 bg-emerald-100"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-none rounded-r"
                    onClick={handleCancelPir}
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => isAdminSuper && setIsEditingPir(true)}
                disabled={!isAdminSuper}
                className={`group flex items-center gap-2 bg-slate-50 border px-3 py-1.5 rounded-md transition-colors ${
                  isAdminSuper
                    ? "hover:bg-emerald-50 hover:border-emerald-200 border-slate-200"
                    : "opacity-90 cursor-not-allowed border-slate-200"
                }`}
                title={
                  isAdminSuper
                    ? "Edit Nilai Pengali (PIR)"
                    : "PIR Periode Aktif"
                }
              >
                <div
                  className={`p-1 rounded-sm transition-colors ${isAdminSuper ? "bg-emerald-100/80 text-emerald-600 group-hover:bg-emerald-200/50" : "bg-slate-200 text-slate-500"}`}
                >
                  <Coins className="w-3.5 h-3.5" />
                </div>
                <div className="flex flex-col items-start leading-none">
                  <span
                    className={`text-[10px] font-bold uppercase tracking-wider mb-0.5 ${isAdminSuper ? "text-slate-400 group-hover:text-emerald-500" : "text-slate-400"}`}
                  >
                    Nilai PIR
                  </span>
                  <span
                    className={`text-sm font-bold tabular-nums flex items-center gap-1.5 ${isAdminSuper ? "text-slate-700 group-hover:text-emerald-700" : "text-slate-600"}`}
                  >
                    {formatRupiah(pirValue)}
                    {isAdminSuper && (
                      <PenLine className="w-3 h-3 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                    )}
                  </span>
                </div>
              </button>
            )}
          </div>
        </ProtectRole>

        <Separator
          orientation="vertical"
          className="h-8 hidden sm:block bg-slate-200"
        />

        {/* 2. Switcher Periode */}
        <div className="flex items-center gap-2">
          <div className="bg-blue-100/50 p-1.5 rounded-md text-blue-600 hidden sm:block border border-blue-200/50">
            <Calendar className="w-4 h-4" />
          </div>
          <Select
            value={activePeriod}
            disabled={!isAdminSuper}
            onValueChange={(val) => {
              const match = masterPeriodes?.find(
                (p) => `${p.tahun}-${p.semester}` === val,
              );
              setActivePeriod(val, match?.master_periode_id);
            }}
          >
            <SelectTrigger className="w-35 sm:w-45 h-9 text-sm font-semibold bg-slate-50 border-slate-200 focus:ring-blue-500 hover:bg-slate-100 transition-colors disabled:opacity-90 disabled:cursor-not-allowed">
              <SelectValue placeholder="Pilih Periode" />
            </SelectTrigger>
            <SelectContent
              align="end"
              className="font-medium max-h-80 overflow-y-auto"
            >
              {isMasterPeriodesLoading ? (
                <div className="flex items-center justify-center py-4 px-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                uniquePeriods?.map((periode) => (
                  <SelectItem
                    key={periode.master_periode_id}
                    value={`${periode.tahun}-${periode.semester}`}
                  >
                    {periode.semester === 1 ? "Ganjil" : "Genap"}{" "}
                    {periode.tahun}/{parseInt(periode.tahun) + 1}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>

          <ProtectRole allowedRoles={[ROLES.ADMIN_SUPER]}>
            {/* Dialog Copy Periode */}
            <Dialog
              open={isCopyPeriodModalOpen}
              onOpenChange={setIsCopyPeriodModalOpen}
            >
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  disabled={!isAdminSuper}
                  className="h-9 w-9 bg-slate-50 hover:bg-blue-50 hover:text-blue-600 border-slate-200"
                  title="Duplikat Setting Periode"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-106.25">
                <DialogHeader>
                  <DialogTitle>Duplikat Setting Periode</DialogTitle>
                  <DialogDescription>
                    Salin semua pengaturan (PIR, dll) dari satu periode ke
                    periode lainnya.
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid gap-2">
                    <Label htmlFor="source">Periode Sumber (Copy dari)</Label>
                    <Select
                      value={copyData.source_periode_id}
                      onValueChange={(val) =>
                        setCopyData({ ...copyData, source_periode_id: val })
                      }
                    >
                      <SelectTrigger id="source" className="w-full">
                        <SelectValue placeholder="Pilih Periode Sumber" />
                      </SelectTrigger>
                      <SelectContent>
                        {uniquePeriods?.map((p) => (
                          <SelectItem
                            key={p.master_periode_id}
                            value={p.master_periode_id.toString()}
                          >
                            {p.semester === 1 ? "Ganjil" : "Genap"} {p.tahun}/
                            {parseInt(p.tahun) + 1}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="target">Periode Tujuan (Copy ke)</Label>
                    <Select
                      value={copyData.target_periode_id}
                      onValueChange={(val) =>
                        setCopyData({ ...copyData, target_periode_id: val })
                      }
                    >
                      <SelectTrigger id="target" className="w-full">
                        <SelectValue placeholder="Pilih Periode Tujuan" />
                      </SelectTrigger>
                      <SelectContent>
                        {uniquePeriods?.map((p) => (
                          <SelectItem
                            key={p.master_periode_id}
                            value={p.master_periode_id.toString()}
                          >
                            {p.semester === 1 ? "Ganjil" : "Genap"} {p.tahun}/
                            {parseInt(p.tahun) + 1}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsCopyPeriodModalOpen(false)}
                  >
                    Batal
                  </Button>
                  <Button
                    type="button"
                    onClick={handleSaveCopyPeriod}
                    disabled={
                      copyPeriodeMutation.isPending ||
                      !copyData.source_periode_id ||
                      !copyData.target_periode_id
                    }
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {copyPeriodeMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Proses...
                      </>
                    ) : (
                      "Duplikat"
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Dialog Tambah Periode */}
            <Dialog
              open={isAddPeriodModalOpen}
              onOpenChange={setIsAddPeriodModalOpen}
            >
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  disabled={!isAdminSuper}
                  className="h-9 w-9 bg-slate-50 hover:bg-slate-100 border-slate-200"
                  title="Tambah Periode Baru"
                >
                  <Plus className="h-4 w-4 text-slate-600" />
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-106.25">
                <DialogHeader>
                  <DialogTitle>Tambah Periode Baru</DialogTitle>
                  <DialogDescription>
                    Masukkan detail periode yang ingin ditambahkan. Klik simpan
                    setelah selesai.
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="tahun" className="text-right">
                      Tahun
                    </Label>
                    <Input
                      id="tahun"
                      placeholder="Contoh: 2027"
                      className="col-span-3"
                      value={newPeriodData.tahun}
                      onChange={(e) =>
                        setNewPeriodData({
                          ...newPeriodData,
                          tahun: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="semester" className="text-right">
                      Semester
                    </Label>
                    <Select
                      value={newPeriodData.semester}
                      onValueChange={(val) =>
                        setNewPeriodData({ ...newPeriodData, semester: val })
                      }
                    >
                      <SelectTrigger className="col-span-3">
                        <SelectValue placeholder="Pilih Semester" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Ganjil">Ganjil</SelectItem>
                        <SelectItem value="Genap">Genap</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="pir" className="text-right">
                      PIR
                    </Label>
                    <div className="col-span-3 relative">
                      <span className="absolute left-3 top-2.5 text-slate-500 text-sm">
                        Rp
                      </span>
                      <Input
                        id="pir"
                        type="number"
                        className="pl-9"
                        value={newPeriodData.pir}
                        onChange={(e) =>
                          setNewPeriodData({
                            ...newPeriodData,
                            pir: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="status" className="text-right">
                      Status
                    </Label>
                    <div className="col-span-3 flex items-center space-x-2">
                      <Switch
                        id="status"
                        checked={newPeriodData.isActive}
                        onCheckedChange={(val) =>
                          setNewPeriodData({ ...newPeriodData, isActive: val })
                        }
                      />
                      <Label htmlFor="status">
                        {newPeriodData.isActive ? "Aktif" : "Nonaktif"}
                      </Label>
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsAddPeriodModalOpen(false)}
                  >
                    Batal
                  </Button>
                  <Button type="button" onClick={handleSaveNewPeriod}>
                    Simpan
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </ProtectRole>
        </div>
      </div>
    </header>
  );
}
